import throttle from 'lodash/throttle';
import { useCallback, useMemo } from 'react';
import { MediaEventType } from 'api/beacon';
import { useWidget } from 'state/widget/context';
import { MEDIA_PLAYBACK_HEARTBEAT_MILLIS } from 'utils/constants';
import { MediaTracking } from '../types';
import useTrackEvent from './useTrackEvent';
import { isSponsored } from './utils';

export default function useMediaEventTracking(): MediaTracking {
  const trackEvent = useTrackEvent();
  const {
    state: { units = {} },
  } = useWidget();

  const createProperties = useCallback(
    (resultId: string, positionMillis: number) => {
      return {
        mediaProperties: {
          resultId,
          isSponsored: isSponsored(resultId, units),
          position: positionMillis,
        },
      };
    },
    [units],
  );

  const createMediaEventTracker = useCallback(
    (event: MediaEventType) => {
      return (resultId: string, positionMillis: number) => {
        trackEvent({
          event,
          properties: createProperties(resultId, positionMillis),
        });
      };
    },
    [createProperties, trackEvent],
  );

  const trackMediaPlaybackStarted = useMemo(
    () => createMediaEventTracker('mediaPlaybackStarted'),
    [createMediaEventTracker],
  );

  const trackMediaPlaybackHeartbeat = useMemo(
    () =>
      throttle(
        createMediaEventTracker('mediaPlaybackHeartbeat'),
        MEDIA_PLAYBACK_HEARTBEAT_MILLIS,
        { trailing: false },
      ),
    [createMediaEventTracker],
  );

  const trackMediaPaused = useMemo(
    () => createMediaEventTracker('mediaPaused'),
    [createMediaEventTracker],
  );

  const trackMediaCompleted = useMemo(
    () => createMediaEventTracker('mediaCompleted'),
    [createMediaEventTracker],
  );

  const trackMediaSeekStarted = useMemo(
    () => createMediaEventTracker('mediaSeekStarted'),
    [createMediaEventTracker],
  );

  const trackMediaSeekCompleted = useMemo(
    () => createMediaEventTracker('mediaSeekCompleted'),
    [createMediaEventTracker],
  );

  const trackMediaBufferStarted = useMemo(
    () => createMediaEventTracker('mediaBufferStarted'),
    [createMediaEventTracker],
  );

  const trackMediaBufferCompleted = useMemo(
    () => createMediaEventTracker('mediaBufferCompleted'),
    [createMediaEventTracker],
  );

  return useMemo(
    () => ({
      trackMediaPlaybackStarted,
      trackMediaPlaybackHeartbeat,
      trackMediaPaused,
      trackMediaCompleted,
      trackMediaSeekStarted,
      trackMediaSeekCompleted,
      trackMediaBufferStarted,
      trackMediaBufferCompleted,
    }),
    [
      trackMediaBufferCompleted,
      trackMediaBufferStarted,
      trackMediaCompleted,
      trackMediaPaused,
      trackMediaPlaybackHeartbeat,
      trackMediaPlaybackStarted,
      trackMediaSeekCompleted,
      trackMediaSeekStarted,
    ],
  );
}
